import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { RootState } from '../../models/RootState';
import './FancyTable.scss';
import { EventData, FancyMarkets } from '../../models/EventData';
import { Accordion, AccordionSummary, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import MarketOddBtn from '../MarketOddBtn/marketOddBtn';
import ExpandLessSharp from '@mui/icons-material/ExpandLessSharp';
import moment from 'moment';
import { getFancyMarkets } from '../../store/exchangeSports/exchangeSportsAction';
import ExchBetSlip from '../ExchBetSlip/ExchBetSlip';
import { BsData, UserBet } from '../../models/ExchBetSlip';
import { addExchangeBet } from '../../store/exchBetSlip/exchBetSlipAction';
import Modal from '../Modal/Modal';
import FancyBookView from './FancyBookView/FancyBookView';
import { useTranslation } from '../../hooks/useTranslation';
import { UserDetails } from '../../models/UserDetails';



type StoreProps = {
  bets: BsData[];
  addExchangeBet: (data: BsData) => void;
  fancyMarkets: FancyMarkets[];
  eventData: EventData;
  openBets: UserBet[];
  isOneClickBettingEnabled: boolean;
  userDetails?: UserDetails;
  dynamicTextColors?: any;
};

type OddsInfoMsg = {
  launch: boolean;
  oddsType: string;
  eventTypeID: string;
};

const FMTable: React.FC<StoreProps> = (props) => {
  const { t } = useTranslation();
  const {
    fancyMarkets,
    eventData,
    bets,
    addExchangeBet,
    openBets,
    isOneClickBettingEnabled,
    userDetails,
    dynamicTextColors
  } = props;

  // Get dynamic text color classes from Redux instead of calling function
  const accordionHeaderTextColorClass = dynamicTextColors?.accordionHeaderTextColorClass || '';
  const activeTabTextColorClass = dynamicTextColors?.ionButtonTextColorClass || '';
  const tableRowTextColorClass = dynamicTextColors?.tableRowTextColorClass || '';
  
  // tabTextColorClass and headerTextColorClass both use --ion-background-color (same as accordionHeaderTextColorClass)
  const tabTextColorClass = accordionHeaderTextColorClass;
  const headerTextColorClass = accordionHeaderTextColorClass;

  const disabledStatus = ['suspended', 'suspended-manually', 'ball_running'];
  const [selectedRow, setSelectedRow] = useState<string>('');
  const [fancyCategories, setFancyCategories] = useState<Set<string>>(new Set());
  const [showBooksModal, setShowBooksModal] = useState<boolean>(false);
  const [fancyBookOutcomeId, setFancyBookOutcomeId] = useState<string>("");
  const [selectedFancyBookMarket, setSelectedFancyBookMarket] = useState<FancyMarkets | null>(null);
  const [tabVal, setTabVal] = useState(0);
  const [filteredFancyMarketsData, setFilteredFancyMarketsData] = useState<FancyMarkets[]>([]);
  const fancyCategoriesOrder = [
    { fancyCategory: 'All', value: 'ALL' },
    { fancyCategory: 'sessions', value: 'SESSIONS' },
    { fancyCategory: 'wpmarket', value: 'W/P MARKET' },
    { fancyCategory: 'extramarket', value: 'EXTRA MARKET' },
    { fancyCategory: 'oddeven', value: 'ODD/EVEN' },
  ];
  const [exposureMap, setExposureMap] = useState<any>({});

  useEffect(() => {
    let localFancyCategories = new Set<string>();
    for (const fm of fancyMarkets) {
      localFancyCategories.add(fm.category);
    }
    fancyMarkets.map((fm) => localFancyCategories.add(fm.category));
    setFancyCategories(localFancyCategories);
  }, [fancyMarkets]);

  const handletabs = useCallback(async (localFancyCategory: string) => {
    setFilteredFancyMarketsData(
      localFancyCategory === 'All'
        ? fancyMarkets
        : fancyMarkets.filter((fm) => {
          return fm.category === localFancyCategory;
        })
    );
  }, []);

  useEffect(() => {
    handletabs('All');
  }, []);

  useEffect(() => {
    calculateExposure(openBets)
  }, [openBets]);

  const calculateExposure = (bets: UserBet[]) => {
    let newExposureMap: any = {};

    // Step 1: Group runners per market
    let marketRunnersMap: { [key: string]: Set<number> } = {};

    bets.forEach((bet) => {
      if (!marketRunnersMap[bet.marketId]) {
        marketRunnersMap[bet.marketId] = new Set();
      }
      marketRunnersMap[bet.marketId].add(bet.sessionOutcome);
      marketRunnersMap[bet.marketId].add(bet.sessionOutcome - 1);
    });

    bets.forEach((bet) => {
      if (bet.marketType === "FANCY") {
        const marketId = bet.marketId;

        if (!newExposureMap[marketId]) {
          newExposureMap[marketId] = {};
        }

        const uniqueRunners = Array.from(marketRunnersMap[marketId]);

        uniqueRunners.forEach((runnerId: any) => {
          if (!newExposureMap[marketId][runnerId]) {
            newExposureMap[marketId][runnerId] = {
              runnerId: runnerId.toString(),
              userRisk: 0,
            };
          }
        });

        // Apply correct risk calculation based on bet type
        uniqueRunners.forEach((runnerId: any) => {
          if (bet.betType === "BACK") {
            if (runnerId < bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk -= bet.stakeAmount;
            } else if (runnerId >= bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk += (bet?.oddValue * 0.01) * bet.stakeAmount;
            }
          } else if (bet.betType === "LAY") {
            if (runnerId < bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk += bet.stakeAmount;
            } else if (runnerId >= bet.sessionOutcome) {
              newExposureMap[marketId][runnerId].userRisk -= (bet?.oddValue * 0.01) * bet.stakeAmount;
            }
          }
        });
      }
    });
    setExposureMap(newExposureMap);
  };

  const getFancyMarketsByGroup = (category: string) => {
    return fancyMarkets
      .filter((fm) => fm.category === category)
      .sort((a, b) => {
        const aSort = a?.sort ? Number(a.sort) : 0;
        const bSort = b?.sort ? Number(b.sort) : 0;
        if (aSort !== bSort) {
          return aSort - bSort;
        }
        const aDesc = a.marketName;
        const bDesc = b.marketName;
        return aDesc.localeCompare(bDesc);
      });
  };

  return (
    <>
      <div className="fm-table-ctn">
        <div className="fm-table-content table-ctn">
          <TableContainer component={Paper}>
            <Table className="fm-table">
              <TableHead>
                <TableRow>
                  <TableCell className="tabs-table-cell" colSpan={12}>
                    <div className={`tabs-fancy ${tabTextColorClass} ${tabTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
                      {fancyCategories.size > 0 ? (
                        <span
                          className={`${tabVal === 0 ? 'sel-tab' : 'ind-tab'} ${tabVal === 0 ? activeTabTextColorClass : tabTextColorClass} ${tabVal === 0 ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : (tabTextColorClass === 'dark-text' ? 'text-black' : 'text-white')}`}
                          onClick={() => {
                            handletabs('All');
                            setTabVal(0);
                          }}
                          style={{ color: tabVal === 0 ? (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF') : (tabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF') }}
                        >
                          <div>{t("common.all")}</div>
                        </span>
                      ) : null}
                      {fancyCategoriesOrder.map((fc, index) => {
                        return fancyCategories.has(fc.fancyCategory) ? (
                          <span
                            className={`${tabVal === index + 1 ? 'sel-tab' : 'ind-tab'} ${tabVal === index + 1 ? activeTabTextColorClass : tabTextColorClass} ${tabVal === index + 1 ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : (tabTextColorClass === 'dark-text' ? 'text-black' : 'text-white')}`}
                            onClick={() => {
                              setTabVal(index + 1);
                              handletabs(fc.fancyCategory);
                            }}
                            style={{ color: tabVal === index + 1 ? (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF') : (tabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF') }}
                          >
                            <div>{fc.value}</div>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFancyMarketsData &&
                  filteredFancyMarketsData.length > 0 ? (
                  <>
                    {fancyCategoriesOrder.map((group) => (
                      <>
                        {filteredFancyMarketsData.filter(
                          (fm) =>
                            fm.category === group.fancyCategory &&
                            fancyCategories.has(group.fancyCategory)
                        ).length > 0 ? (
                          <>
                            <Accordion
                              defaultExpanded={true}
                              className={`markets-accordian ${headerTextColorClass} ${headerTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
                            >
                              <AccordionSummary
                                expandIcon={
                                  <ExpandLessSharp className={`expand-icon ${headerTextColorClass} ${headerTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: headerTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }} />
                                }
                                aria-controls="panel1a-content"
                              >
                                <FancyHeaderRow
                                  groupName={group.value}
                                  headerTextColorClass={headerTextColorClass}
                                />
                              </AccordionSummary>
                              <FancyHeaderRow
                                groupName={group.value}
                                className="row-hidden"
                                headerTextColorClass={headerTextColorClass}
                              />
                              {getFancyMarketsByGroup(group.fancyCategory).map(
                                (fmMarket, idx) => {
                                  return (
                                    <>
                                      <FancyMarketRow
                                        isOneClickBettingEnabled={isOneClickBettingEnabled}
                                        index={idx}
                                        fancyMarket={fmMarket}
                                        disabledStatus={disabledStatus}
                                        eventData={eventData}
                                        addExchangeBet={addExchangeBet}
                                        bets={bets}
                                        selectedRow={selectedRow}
                                        setShowBooksModal={() => {
                                          setFancyBookOutcomeId(
                                            fmMarket?.marketId
                                          );
                                          setSelectedFancyBookMarket(fmMarket);
                                          setShowBooksModal(true);
                                        }}
                                        exposureMap={exposureMap}
                                        setSelectedRow={setSelectedRow}
                                        min={fmMarket?.limits?.minBetValue}
                                        max={fmMarket?.limits?.maxBetValue}
                                        tableRowTextColorClass={tableRowTextColorClass}
                                      />
                                    </>
                                  );
                                }
                              )}
                            </Accordion>
                          </>
                        ) : null}
                      </>
                    ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} style={{ background: 'transparent' }}>
                      <div className={`no-fancy-msg ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                        {t("sports.messages.noFancyMarkets")}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Modal
            open={showBooksModal}
            closeHandler={() => {
              setShowBooksModal(false);
              setFancyBookOutcomeId("");
              setSelectedFancyBookMarket(null);
            }}
            customClass="fancy-book-dialog"
            title={t("sports.bookList")}
            size="sm"
          >
            <FancyBookView
              fancyMarket={selectedFancyBookMarket || undefined}
              exposureMap={
                exposureMap &&
                  exposureMap[fancyBookOutcomeId]
                  ? exposureMap[fancyBookOutcomeId]
                  : {}
              }
            />
          </Modal>
        </div>
      </div>
    </>
  );
};

type FancyHeaderRowProps = {
  groupName: string;
  className?: string;
  headerTextColorClass?: string;
};

const FancyHeaderRow: React.FC<FancyHeaderRowProps> = (props) => {
  const { t } = useTranslation();
  const { groupName, className, headerTextColorClass = '' } = props;
  const tableFields = [
    {
      key: 'groupName',
      Label: groupName,
      className: 'market-name-cell-head',
      align: 'left',
    },
    {
      key: 'odds-no',
      Label: '',
      className: 'odds-cell-head book-btn-cell',
      align: 'center',
    },
    {
      key: 'odds-no',
      Label: t('common.no'),
      className: 'odds-cell-head odds-yes-cell',
      align: 'center',
    },
    {
      key: 'odds-yes',
      Label: t('common.yes'),
      className: 'odds-cell-head odds-yes-cell',
      align: 'center',
    },
    {
      key: 'limits',
      Label: '',
      className: 'odds-cell-head limits-cell',
      align: 'center',
    },
  ];

  return (
    <TableRow className={`header-row ${className} ${headerTextColorClass} ${headerTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
      {tableFields.map((tF, index) => (
        <TableCell
          key={tF.key + index}
          align={tF.align === 'left' ? 'left' : 'center'}
          className={`${tF.className} ${headerTextColorClass} ${headerTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
          style={{ color: headerTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}
        >
          {tF.key === 'odds-no' ||
            tF.key === 'odds-yes' ||
            tF.key === 'groupName' ? (
            <div className={`${tF.key.toLowerCase()}-cell ${headerTextColorClass} ${headerTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: headerTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
              {tF.Label?.toUpperCase()}
            </div>
          ) : null}
        </TableCell>
      ))}
    </TableRow>
  );
};

type FancyMarketRowProps = {
  fancyMarket: FancyMarkets;
  index: number;
  eventData: EventData;
  disabledStatus: string[];
  selectedRow: string;
  setSelectedRow: (data: any) => void;
  addExchangeBet: (data: BsData) => void;
  bets: BsData[];
  exposureMap: any;
  setShowBooksModal: () => void;
  // fetchBetLimits: (mId, mcategory) => void;
  max: number;
  min: number;
  isOneClickBettingEnabled: boolean;
  tableRowTextColorClass?: string;
};

const FancyMarketRow: React.FC<FancyMarketRowProps> = (props) => {
  const { t } = useTranslation();
  const {
    fancyMarket,
    index,
    disabledStatus,
    eventData,
    addExchangeBet,
    setSelectedRow,
    setShowBooksModal,
    bets,
    // fetchBetLimits,
    exposureMap,
    min,
    max,
    isOneClickBettingEnabled,
    tableRowTextColorClass = ''
  } = props;
  const isOddEvenCategory = fancyMarket?.oddType === 'ODD_EVEN';
  const oddEvenDisplayRate = fancyMarket?.noRate !== undefined && fancyMarket?.noRate !== null
    ? ((Number(fancyMarket.noRate) + 100) / 100).toFixed(2)
    : '0.00';
  const oddEvenDisplayMaxBet = fancyMarket?.limits?.maxBetValue || 0;

  return (
    <>
      <TableRow key={'row-' + index} className={`${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}>
        <TableCell className={`market-name-cell ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} key={'row-' + index + 'cell-1'} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
          <div className={`market ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
            {fancyMarket.marketName}{' '}
          </div>
        </TableCell>
        <TableCell
          className={`odds-cell book-btn-cell ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`}
          key={'row-' + index + 'cell-4'}
          style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}
        >
          <Button
            className="fancy-book-btn"
            onClick={() => {
              if (
                exposureMap &&
                exposureMap[`${fancyMarket.marketId}`]
              )
                setShowBooksModal();
            }}
            disabled={
              !(
                exposureMap &&
                exposureMap[`${fancyMarket.marketId}`]
              )
            }
          >
            {t("sports.book")}
          </Button>
        </TableCell>
        <TableCell className={`odds-cell ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} key={'row-' + index + 'cell-2'} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : isOddEvenCategory ? oddEvenDisplayRate : fancyMarket.noValue ? Number(fancyMarket.noValue).toFixed() : 0
              }
              mainValueClass="runs"
              subValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : isOddEvenCategory ? oddEvenDisplayMaxBet : fancyMarket.noRate ? fancyMarket.noRate : 0}
              subValueClass="odds"
              showSubValueinKformat={isOddEvenCategory}
              oddType={isOddEvenCategory ? "odds-yes-cell" : "odds-no-cell"}
              valueType="fancyMarketOdds"
              disable={disabledStatus.includes(fancyMarket.status.toLowerCase()) ||
                moment(eventData.openDate).diff(moment(), 'minutes') > 60 * 24 * 2}
              onClick={() => {
                if (
                  !disabledStatus.includes(fancyMarket.status.toLowerCase())
                ) {
                  setSelectedRow(fancyMarket.marketName + 'FM');
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: fancyMarket?.marketId,
                    marketType: "FANCY",
                    marketName: fancyMarket?.marketName,
                    oddValue: fancyMarket?.noRate,
                    sessionOutcome: fancyMarket?.noValue,
                    betType: isOddEvenCategory ? "BACK" : "LAY",
                    stakeAmount: 0,
                    runnerId: fancyMarket?.marketId,
                    runnerName: fancyMarket?.marketName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.fancyProvider || ""
                  });
                }
              }}
            />
          </div>
        </TableCell>

        <TableCell className={`odds-cell ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} key={'row-' + index + 'cell-3'} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
          <div className="odds-block">
            <MarketOddBtn
              mainValue={
                disabledStatus.includes(fancyMarket.status.toLowerCase()) ?
                  0 : isOddEvenCategory ? oddEvenDisplayRate : Number(fancyMarket.yesValue).toFixed()}
              mainValueClass="runs"
              subValue={
                isOddEvenCategory
                  ? oddEvenDisplayMaxBet
                  : fancyMarket.yesRate
              }
              subValueClass="odds"
              showSubValueinKformat={isOddEvenCategory}
              oddType="odds-yes-cell"
              valueType="fancyMarketOdds"
              disable={disabledStatus.includes(fancyMarket.status.toLowerCase()) ||
                moment(eventData.openDate).diff(moment(), 'minutes') > 60 * 24 * 2}
              onClick={() => {
                if (
                  !disabledStatus.includes(fancyMarket.status.toLowerCase())
                ) {
                  setSelectedRow(fancyMarket.marketName + 'FM');
                  addExchangeBet({
                    eventId: eventData?.eventId,
                    eventName: eventData?.eventName,
                    marketId: fancyMarket?.marketId,
                    marketType: "FANCY",
                    marketName: fancyMarket?.marketName,
                    oddValue: fancyMarket.yesRate,
                    sessionOutcome: fancyMarket.yesValue,
                    betType: "BACK",
                    stakeAmount: 0,
                    runnerId: fancyMarket?.marketId,
                    runnerName: fancyMarket?.marketName,
                    competitionId: eventData?.competitionId,
                    sportId: eventData?.sportId,
                    maxLimit: max,
                    minLimit: min,
                    provider: eventData?.markets?.fancyProvider || ""
                  });
                }
              }}
            />
          </div>
        </TableCell>

        <TableCell className={`limits-cell ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
          <div className={`limits-data ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
            <div className={`row mob-view ${tableRowTextColorClass} ${tableRowTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: tableRowTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
              <div>
                {t("sports.min")}:{' '}
                {fancyMarket?.limits?.minBetValue}
              </div>{' '}
              <div>
                {' '}
                {t("sports.max")}:{' '}
                {fancyMarket?.limits?.maxBetValue % 1000 === 0 ? fancyMarket?.limits?.maxBetValue / 1000 + "K" : fancyMarket?.limits?.maxBetValue}
              </div>
            </div>
          </div>
        </TableCell>
        {disabledStatus.includes(fancyMarket.status.toLowerCase()) ? (
          <div className="disabled-odds-cell" key={'row-' + index + 'cell-5'}>
            {fancyMarket.status.toLowerCase().includes('suspended')
              ? t("sports.suspended")
              : fancyMarket.status.replace('_', ' ')}
          </div>
        ) : null}
      </TableRow>
      {!isOneClickBettingEnabled && bets?.length > 0 &&
        bets?.[0]?.marketName === fancyMarket?.marketName &&
        bets?.[0]?.marketId === fancyMarket?.marketId &&
        // isMobile ? (
        <TableRow className="inline-betslip">
          <TableCell colSpan={12}>
            {' '}
            <ExchBetSlip balance={0} exposure={0} />{' '}
          </TableCell>
        </TableRow>
        // ) : null
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    bets: state.exchBetSlip.bets,
    eventData: state.exchangeSports.eventInfo,
    fancyMarkets: getFancyMarkets(state.exchangeSports.eventInfo),
    openBets: state?.exchBetSlip?.openBets,
    isOneClickBettingEnabled: state.exchBetSlip.isOneClickBettingEnabled,
    userDetails: state.userDetails?.userDetails,
    dynamicTextColors: state.userDetails?.dynamicTextColors,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    addExchangeBet: (data: BsData) => dispatch(addExchangeBet(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FMTable);
